import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { addCountryMaster, updateCountryMaster } from "../../../api/superAdminApi";

const emptyForm = () => ({
  country_name: "",
  country_code: "",
  currency_code: "",
  currency_symbol: "",
});

export default function AddCountryModal({ open, onClose, onSuccess, initialData = null }) {
  const isEdit = Boolean(initialData?.cid);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialData?.cid) {
      setForm({
        country_name: initialData.country_name || "",
        country_code: initialData.country_code || "",
        currency_code: initialData.currency_code || "",
        currency_symbol: initialData.currency_symbol || "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, initialData]);

  const canSave = Boolean(form.country_name.trim());

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    const payload = {
      country_name: form.country_name.trim(),
      country_code: form.country_code.trim(),
      currency_code: form.currency_code.trim(),
      currency_symbol: form.currency_symbol.trim(),
    };
    try {
      const res = isEdit
        ? await updateCountryMaster(initialData.cid, payload)
        : await addCountryMaster(payload);
      const body = res.data;
      if (!isEdit && body?.data?.alreadyExists) {
        toast.info(body.message || "Country already exists");
      } else if (body?.status_code === 200) {
        toast.success(body.message || (isEdit ? "Country updated successfully" : "Country added successfully"));
        onSuccess?.();
        onClose();
      } else {
        toast.error(body?.message || (isEdit ? "Failed to update country." : "Failed to add country."));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || (isEdit ? "Failed to update country." : "Failed to add country.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Update Country" : "Add Country"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Country name"
            required
            fullWidth
            value={form.country_name}
            onChange={(e) => setForm((prev) => ({ ...prev, country_name: e.target.value }))}
          />
          <TextField
            label="Country code"
            fullWidth
            placeholder="e.g. IN, US"
            value={form.country_code}
            onChange={(e) => setForm((prev) => ({ ...prev, country_code: e.target.value }))}
          />
          <TextField
            label="Currency code"
            fullWidth
            placeholder="e.g. INR, USD"
            value={form.currency_code}
            onChange={(e) => setForm((prev) => ({ ...prev, currency_code: e.target.value }))}
          />
          <TextField
            label="Currency symbol"
            fullWidth
            placeholder="e.g. ₹, $"
            value={form.currency_symbol}
            onChange={(e) => setForm((prev) => ({ ...prev, currency_symbol: e.target.value }))}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEdit ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
